import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import Sidebar from '../components/Sidebar';
import ChatWindow from '../components/ChatWindow';
import AdminPanel from './AdminPanel';
import TenantSettings from './TenantSettings';
import Workflows from './Workflows';
import Tasks from './Tasks';
import Knowledge from './Knowledge';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function ChatLayout() {
  const { token, user } = useAuth();
  const { activeTenant, openSwitcher, personalMode } = useTenant();
  // Default für neue Chats: RAG aus. User aktiviert pro Chat über den Toggle.
  const [newChatUseRag, setNewChatUseRag] = useState(false);
  // Web-Suche (Privat-Modus / SearXNG): ebenfalls aus by default, pro Chat anschaltbar.
  const [newChatUseWeb, setNewChatUseWeb] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showWorkflows, setShowWorkflows] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [showTenantSettings, setShowTenantSettings] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [modelsOffline, setModelsOffline] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [saveHistory, setSaveHistory] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const activeChatRef = useRef(null);
  const isStreamingRef = useRef(false);
  const abortControllerRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Load models
  useEffect(() => {
    fetch(`${API_BASE}/api/models`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        if (data.models?.length) {
          setModels(data.models);
          setSelectedModel(data.models[0].name);
          setModelsOffline(false);
        } else {
          setModels([]);
          setModelsOffline(true);
        }
      })
      .catch(() => {
        setModels([]);
        setModelsOffline(true);
      });
  }, [token]);

  // Load user preferences
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setSaveHistory(data.saveHistory))
      .catch(console.error);
  }, [token]);

  // Load chats
  const loadChats = useCallback(() => {
    fetch(`${API_BASE}/api/chat`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(setChats)
      .catch(console.error);
  }, [token]);

  useEffect(() => { loadChats(); }, [loadChats]);

  // Keep ref in sync for visibility handler
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Re-fetch active chat when page becomes visible again (e.g., after screen off)
  // Skip if currently streaming to avoid overwriting optimistic state
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeChatRef.current && !isStreamingRef.current) {
        fetch(`${API_BASE}/api/chat/${activeChatRef.current._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(res => res.json())
          .then(chat => setActiveChat(chat))
          .catch(console.error);
        loadChats();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token, loadChats]);

  const newChat = () => {
    setActiveChat(null);
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const startChat = async (model, firstMessage) => {
    if (!model) return;
    isStreamingRef.current = true;
    const startTime = Date.now();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setStreaming(true);

    try {
      // Create chat — RAG nur wenn ein Mandant aktiv ist (sonst gibt es keinen
      // Knowledge-Index). Im Privat-Modus immer aus.
      const useRag = !!(newChatUseRag && activeTenant);
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model,
          tenantId: activeTenant?._id || null,
          useRag,
          // Web-Suche braucht keinen Tenant — auch im Privat-Modus nutzbar.
          useWeb: !!newChatUseWeb,
        })
      });
      const chat = await res.json();
      setChats(prev => [chat, ...prev]);
      setActiveChat(chat);

      // Send first message immediately
      const userMsg = { role: 'user', content: firstMessage, createdAt: new Date() };
      setActiveChat(prev => ({
        ...prev,
        messages: [...prev.messages, userMsg]
      }));

      const msgRes = await fetch(`${API_BASE}/api/chat/${chat._id}/message`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content: firstMessage }),
        signal: abortController.signal
      });

      const reader = msgRes.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let assistantSources = [];

      setActiveChat(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: '', createdAt: new Date(), duration: null, sources: [] }]
      }));

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(l => l.startsWith('data: '));

        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.sources) {
              assistantSources = data.sources;
              setActiveChat(prev => ({
                ...prev,
                messages: prev.messages.map((m, i) =>
                  i === prev.messages.length - 1 ? { ...m, sources: data.sources } : m
                )
              }));
            }
            if (data.content) {
              assistantContent += data.content;
              const currentContent = assistantContent;
              setActiveChat(prev => ({
                ...prev,
                messages: prev.messages.map((m, i) =>
                  i === prev.messages.length - 1 ? { ...m, content: currentContent } : m
                )
              }));
            }
            if (data.done) {
              const duration = ((Date.now() - startTime) / 1000).toFixed(1);
              setActiveChat(prev => ({
                ...prev,
                messages: prev.messages.map((m, i) =>
                  i === prev.messages.length - 1 ? { ...m, duration } : m
                )
              }));
              loadChats();
            }
          } catch (e) {
            // skip
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        const duration = ((Date.now() - startTime) / 1000).toFixed(1);
        setActiveChat(prev => ({
          ...prev,
          messages: prev.messages.map((m, i) =>
            i === prev.messages.length - 1
              ? { ...m, duration, stopped: true }
              : m
          )
        }));
        loadChats();
      } else {
        console.error(e);
      }
    } finally {
      isStreamingRef.current = false;
      setStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const selectChat = async (chatId) => {
    const res = await fetch(`${API_BASE}/api/chat/${chatId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const chat = await res.json();
    setActiveChat(chat);
  };

  const deleteChat = async (chatId) => {
    await fetch(`${API_BASE}/api/chat/${chatId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setChats(prev => prev.filter(c => c._id !== chatId));
    if (activeChat?._id === chatId) setActiveChat(null);
  };

  const bulkDelete = async ({ chatIds, all }) => {
    await fetch(`${API_BASE}/api/chat/bulk`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify(all ? { all: true } : { chatIds })
    });
    if (all) {
      setChats([]);
      setActiveChat(null);
    } else {
      const deletedSet = new Set(chatIds);
      setChats(prev => prev.filter(c => !deletedSet.has(c._id)));
      if (activeChat && deletedSet.has(activeChat._id)) setActiveChat(null);
    }
  };

  const renameChat = async (chatId, title) => {
    await fetch(`${API_BASE}/api/chat/${chatId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ title })
    });
    setChats(prev => prev.map(c => c._id === chatId ? { ...c, title } : c));
    if (activeChat?._id === chatId) setActiveChat(prev => ({ ...prev, title }));
  };

  // Toggle RAG für einen bestehenden Chat. Wenn der Chat noch keinen tenantId
  // hat (Chat aus Privat-Modus oder vor dem RAG-Feature angelegt), bekommt er
  // den aktuell aktiven Tenant zugewiesen.
  const toggleChatRag = async (next) => {
    if (!activeChat) return;
    const body = { useRag: !!next };
    if (next && activeTenant && !activeChat.tenantId) {
      body.tenantId = activeTenant._id;
    }
    await fetch(`${API_BASE}/api/chat/${activeChat._id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body)
    });
    setActiveChat(prev => prev ? { ...prev, ...body } : prev);
  };

  // Toggle Web-Suche für einen bestehenden Chat. Kein Tenant nötig.
  const toggleChatWeb = async (next) => {
    if (!activeChat) return;
    const body = { useWeb: !!next };
    await fetch(`${API_BASE}/api/chat/${activeChat._id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body)
    });
    setActiveChat(prev => prev ? { ...prev, ...body } : prev);
  };

  const toggleSaveHistory = async () => {
    const newValue = !saveHistory;
    setSaveHistory(newValue);
    await fetch(`${API_BASE}/api/auth/preferences`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ saveHistory: newValue })
    });
    // If turning off, delete all existing chats
    if (!newValue) {
      await bulkDelete({ all: true });
    }
  };

  const sendMessage = async (content) => {
    if (!activeChat) return;
    isStreamingRef.current = true;
    const startTime = Date.now();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setStreaming(true);

    try {
      // Optimistic update: add user message
      const userMsg = { role: 'user', content, createdAt: new Date() };
      setActiveChat(prev => ({
        ...prev,
        messages: [...prev.messages, userMsg]
      }));

      // Stream response
      const res = await fetch(`${API_BASE}/api/chat/${activeChat._id}/message`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content }),
        signal: abortController.signal
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      const tokenQueue = [];
      let streamDone = false;
      let renderDuration = null;

      // Add empty assistant message
      setActiveChat(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: '', createdAt: new Date(), duration: null, sources: [] }]
      }));

      // Typing renderer: pulls tokens from queue and renders them progressively
      const renderTokens = () => new Promise((resolve) => {
        const render = () => {
          if (tokenQueue.length > 0) {
            const token = tokenQueue.shift();
            assistantContent += token;
            const currentContent = assistantContent;
            setActiveChat(prev => ({
              ...prev,
              messages: prev.messages.map((m, i) =>
                i === prev.messages.length - 1 ? { ...m, content: currentContent } : m
              )
            }));
            setTimeout(render, 15);
          } else if (streamDone) {
            // All tokens rendered, show duration
            if (renderDuration) {
              setActiveChat(prev => ({
                ...prev,
                messages: prev.messages.map((m, i) =>
                  i === prev.messages.length - 1 ? { ...m, duration: renderDuration } : m
                )
              }));
              loadChats();
            }
            resolve();
          } else {
            // Queue empty but stream not done, wait for more
            setTimeout(render, 30);
          }
        };
        render();
      });

      // Start rendering in parallel with reading
      const renderPromise = renderTokens();

      // Read all SSE data and push tokens into queue
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(l => l.startsWith('data: '));

        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.sources) {
              setActiveChat(prev => ({
                ...prev,
                messages: prev.messages.map((m, i) =>
                  i === prev.messages.length - 1 ? { ...m, sources: data.sources } : m
                )
              }));
            }
            if (data.content) {
              tokenQueue.push(data.content);
            }
            if (data.done) {
              renderDuration = ((Date.now() - startTime) / 1000).toFixed(1);
            }
          } catch (e) {
            // skip
          }
        }
      }

      streamDone = true;
      await renderPromise;
    } catch (e) {
      if (e.name === 'AbortError') {
        const duration = ((Date.now() - startTime) / 1000).toFixed(1);
        setActiveChat(prev => ({
          ...prev,
          messages: prev.messages.map((m, i) =>
            i === prev.messages.length - 1
              ? { ...m, duration, stopped: true }
              : m
          )
        }));
        loadChats();
      } else {
        console.error(e);
      }
    } finally {
      isStreamingRef.current = false;
      setStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const closeSidebar = () => setSidebarOpen(false);

  const handleBack = () => {
    setActiveChat(null);
    setSidebarOpen(true);
  };

  const handleSelectChat = async (chatId) => {
    await selectChat(chatId);
    // Close sidebar on mobile after selecting a chat
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="chat-layout">
      <div
        className={`sidebar-backdrop ${!sidebarOpen ? 'hidden' : ''}`}
        onClick={closeSidebar}
      />
      <Sidebar
        chats={chats}
        activeChat={activeChat}
        onNewChat={() => { setShowAdmin(false); setShowWorkflows(false); setShowTasks(false); setShowKnowledge(false); newChat(); }}
        onSelectChat={(id) => { setShowAdmin(false); setShowWorkflows(false); setShowTasks(false); setShowKnowledge(false); handleSelectChat(id); }}
        onDeleteChat={deleteChat}
        onRenameChat={renameChat}
        onBulkDelete={bulkDelete}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        isAdmin={user?.role === 'admin'}
        onOpenAdmin={() => { setShowAdmin(true); setShowWorkflows(false); setShowTasks(false); setShowKnowledge(false); setActiveChat(null); if (window.innerWidth <= 768) setSidebarOpen(false); }}
        onOpenWorkflows={() => { setShowWorkflows(true); setShowAdmin(false); setShowTasks(false); setShowKnowledge(false); setActiveChat(null); if (window.innerWidth <= 768) setSidebarOpen(false); }}
        onOpenTasks={() => { setShowTasks(true); setShowAdmin(false); setShowWorkflows(false); setShowKnowledge(false); setActiveChat(null); if (window.innerWidth <= 768) setSidebarOpen(false); }}
        onOpenKnowledge={() => { setShowKnowledge(true); setShowAdmin(false); setShowWorkflows(false); setShowTasks(false); setActiveChat(null); if (window.innerWidth <= 768) setSidebarOpen(false); }}
        saveHistory={saveHistory}
        onToggleSaveHistory={toggleSaveHistory}
        activeTenant={activeTenant}
        personalMode={personalMode}
        onOpenTenantSwitcher={openSwitcher}
        onOpenTenantSettings={() => setShowTenantSettings(true)}
      />
      {showTenantSettings && (
        <TenantSettings onClose={() => setShowTenantSettings(false)} />
      )}
      {showAdmin ? (
        <AdminPanel onBack={() => setShowAdmin(false)} />
      ) : showWorkflows ? (
        <Workflows onBack={() => setShowWorkflows(false)} defaultModel={selectedModel} />
      ) : showTasks ? (
        <Tasks onBack={() => setShowTasks(false)} />
      ) : showKnowledge ? (
        <Knowledge onBack={() => setShowKnowledge(false)} />
      ) : (
        <ChatWindow
          chat={activeChat}
          onSendMessage={sendMessage}
          onStartChat={startChat}
          onStopGeneration={stopGeneration}
          streaming={streaming}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onBack={handleBack}
          models={models}
          modelsOffline={modelsOffline}
          selectedModel={selectedModel}
          onSelectModel={setSelectedModel}
          ragAvailable={!!activeTenant}
          newChatUseRag={newChatUseRag}
          onToggleNewChatRag={() => setNewChatUseRag(v => !v)}
          onToggleChatRag={toggleChatRag}
          newChatUseWeb={newChatUseWeb}
          onToggleNewChatWeb={() => setNewChatUseWeb(v => !v)}
          onToggleChatWeb={toggleChatWeb}
        />
      )}
    </div>
  );
}
